import dayjs from "dayjs"; // Importing dayjs for date manipulation
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx"; // Importing MobX utilities
import toast from "react-hot-toast";
import URLConstants from "../../constants/url-constants"; // Importing URL constants
import { IObservableInitialState, IOptionShiftSchedule } from "../../models/ICommon"; // Importing common observable state interface
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse"; // Importing API response interfaces
import { IBreakVM, IEvent, IReservationProcessStateVM } from "../../models/response/IReservationResponse"; // Importing reservation response models
import { ICommonState } from "../../models/state/ICommonState"; // Importing common state interface
import { formatMessage } from "../../translations/format-message";
import { AddBreakState } from "../initialState/add-edit-break-state"; // Importing initial state for adding/editing breaks
import * as baseService from "../service/base-service"; // Importing base service
import moment from "moment";
import { useStore } from "../../contexts/store-provider";
import masterManagementStore, { MasterManagementStore } from "./master-management-store";
import { downloadAppointmentsCSV } from "../../constants/appointment-csv-constants";
import { AppointmentProcessState } from "../../constants/enums/appointment-process-state-enum";

export class ReservationSchedulerStore implements ICommonState {
  inProgress = false; // Tracks if a process is in progress
  error = ""; // Stores error messages

  initialStateValue: IObservableInitialState = {
    success: false, // Indicates success state
    error: "", // Error message
    inProgress: false, // Tracks progress state
  };

    reservationProcessStateResponse: any = undefined;
  isPollingActive: boolean = false;
  events: any[] = []; // Stores events
  appointments: any[] = []; // Stores appointments
  breaks: any[] = []; // Stores breaks
  droppedEvents: any[] = []; // Stores dropped events
  staffBreak: IBreakVM | undefined = AddBreakState; // Stores staff break details
  updateBookingTypeState = { ...this.initialStateValue }; // State for updating booking type
  selectedStaffIdWeekly = -1; // Selected staff ID for weekly view

  // Initial states for various operations
  addStaffBreakState = { ...this.initialStateValue };
  getStaffAppointmentsState = { ...this.initialStateValue };
  updateDraggableAppointmentState = { ...this.initialStateValue };
  updateDraggableBreakState = { ...this.initialStateValue };
  getStaffBreakByIdState = { ...this.initialStateValue };
  deleteStaffBreakByIdState = { ...this.initialStateValue };
  updateReservationMemoState = { ...this.initialStateValue };
  updateReservationCancelState = { ...this.initialStateValue };
  getReservationListState = { ...this.initialStateValue };
  
  constructor() {
    makeObservable(this, {
      inProgress: observable, // Observable for progress state
      error: observable, // Observable for error messages
      isPollingActive: observable, // Observable for polling state
      reservationProcessStateResponse: observable, // Observable for reservation process state response
      selectedStaffIdWeekly: observable.deep, // Deep observable for selected staff ID in weekly view
      events: observable.deep, // Deep observable for events
      appointments: observable.deep, // Deep observable for appointments
      breaks: observable.deep, // Deep observable for breaks
      staffBreak: observable.deep, // Deep observable for staff break
      getReservationListState: observable, // Observable for reservation list state
      droppedEvents: observable.deep, // Observable for dropped events
      addStaffBreakState: observable, // Observable for add staff break state
      updateDraggableAppointmentState: observable, // Observable for draggable appointment state
      updateDraggableBreakState: observable, // Observable for draggable break state
      getStaffAppointmentsState: observable, // Observable for staff appointments state
      getStaffBreakByIdState: observable, // Observable for staff break by ID state
      deleteStaffBreakByIdState: observable, // Observable for delete staff break by ID state
      updateReservationMemoState: observable, //Observable for update reservation memo state
      updateReservationCancelState: observable, //Observable for update reservation cancel state
      updateBookingTypeState: observable, // Observable for updating booking type state
      addDroppedEvent: action, // Action for adding dropped events
      GetStaffAppointmentsCSVListService: action, // Action for getting the Staff Appoiuntments Data to convert into the CSV File
      removeDroppedEvent: action, // Action for removing dropped events
      AddStaffBreakService: action, // Action for adding staff break service
      GetStaffAppointmentsListService: action, // Action for getting staff appointments
      UpdateAppointmentDraggableService: action, // Action for updating draggable appointments
      UpdateBreakDraggableService: action, // Action for updating draggable breaks
      DeleteStaffBreaksService: action, // Action for deleting staff breaks
      GetStaffBreakByIdService: action, // Action for getting staff break by ID
      UpdateReservationMemoService: action, //Action for updating Reservation Memo service
      UpdateReservationCancelStatusService: action, // Action for updating Cancel Status Service
      UpdateBookingTypeService: action, // Action for updating booking type
      UpdateReservationPostBreakTimeService: action, // Action for updating reservation post break time
      GetReservationProcessStateService: action, // Action for getting reservation process state
      pollReservationProcessState: action, // Action for polling reservation process state
      stopPolling: action, // Action to stop polling
      resetAddStaffBreakState: action, // Action for resetting add staff break state
      resetGetStaffBreakByIdState: action, // Action for resetting staff break by ID state
      resetUpdateAppointmentState: action, // Action for resetting update appointment state
      resetUpdateStaffBreakState: action, // Action for resetting update staff break state
      resetDeleteStaffBreakState: action, // Action for resetting delete staff break state
      resetUpdateReservationMemoState: action, // Action for resetting the reservation memo update state
      resetUpdateReservationCancelState: action, // Action for resetting the reservation cancel update state
      setSelectedStaffIdWeekly: action, // Action for setting selected staff ID in weekly view
      GetStaffAppointmentsListForDateService: action, // Action for getting staff appointments for a specific date
      resetBookingTypeState: action, // Action for resetting booking type state
      getStaffAppointmentCountByDateRange: action, // Computed property for staff appointment statistics
      removeEvent: action,
      resetDroppedEvents: action,
      addEventBackToOrignal: action,
      getSelectedStaffIdWeekly: computed, // Getter for selected staff ID in weekly view
      getStaffAppointments: computed, // Computed property for staff appointments
      getBreaks: computed, // Computed property for breaks
      staffBreakDetails: computed, // Computed property for staff break details,
      getStaffAppointmentsNonStaff: computed,
      getStaffBreaks: computed,
      getStaffAppointmentsDate: computed, // Computed property for staff appointments by date
    });
  }

  // Adds a dropped event if it doesn't already exist
  addDroppedEvent = (data: any) => {
    const exists = this.droppedEvents.some((event) => event.Id === data.Id);
    if (!exists) {
      this.droppedEvents.push(data);
    }
  };

  // Removes a dropped event by ID
  removeDroppedEvent = (dataId: any) => {
    this.droppedEvents = this.droppedEvents.filter((obj) => obj.Id !== dataId);
  };

  removeEvent = (dataId: any) => {
    this.events = this.events.filter((obj) => obj.Id !== dataId);
  };

  addEventBackToOrignal = (data: any) => {
    const exists = this.events.some((event) => event.Id === data.Id);
    if (!exists) {
      this.events.push(data);
    }
  };

  setSelectedStaffIdWeekly = (id: number) => {
    this.selectedStaffIdWeekly = id; // Setter for selected staff ID in weekly view
  };

  // Fetches staff appointments list from the server
  GetStaffAppointmentsListService = action(
    (StaffId: any, StoreId: number, Date: string, endDate?: string) => {
      this.getStaffAppointmentsState.inProgress = true;
      let url =
        URLConstants.GetStaffAppointments +
        "?StaffId=" +
        StaffId +
        "&StoreId=" +
        StoreId +
        "&Date=" +
        Date +
        "&endDate=" +
        endDate;
      return baseService
        .getRequest(url)
        .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
          runInAction(() => {
            if (response.data.Error) {
              this.getStaffAppointmentsState.error = response.data.Message;
            } else {
              let data = response.data.Data;
              this.events = data.flatMap(
                (appointment: any) => appointment.AppointmentsList || []
              );
              this.breaks = data.flatMap((staff: any) => staff.Breaks || []);
            }
          });
        })
        .catch((err: string) => {
          this.getStaffAppointmentsState.error = err;
        })
        .finally(
          action(() => {
            this.getStaffAppointmentsState.inProgress = false;
          })
        );
    }
  );

    GetStaffAppointmentsCSVListService = action(
    (StaffId: any, StoreId: number, Date: string, endDate?: string) => {
      this.getStaffAppointmentsState.inProgress = true;
      let url =
        URLConstants.GetStaffAppointments + "/CSV" +
        "?StaffId=" +
        StaffId +
        "&StoreId=" +
        StoreId +
        "&Date=" +
        Date +
        "&endDate=" +
        endDate;
      return baseService
        .getRequest(url)
        .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
          runInAction(() => {
            if (response.data.Error) {
              this.getStaffAppointmentsState.error = response.data.Message;
            } else {
              let data = response.data.Data;
              let eventData = data.flatMap(
                (appointment: any) => appointment.AppointmentsList || []
              );
                downloadAppointmentsCSV(eventData,`予約一覧-${moment().format("YYYYMMDD_HHmmss")}`);
            }
          });
        })
        .catch((err: string) => {
          this.getStaffAppointmentsState.error = err;
        })
        .finally(
          action(() => {
            this.getStaffAppointmentsState.inProgress = false;
          })
        );
    }
  );

    // Fetches staff appointments list from the server
  GetStaffAppointmentsListForDateService = action(
    (StaffId: any, StoreId: number, Date: string, endDate?: string) => {
      this.getStaffAppointmentsState.inProgress = true;
      let url =
        URLConstants.GetStaffAppointments +
        "?StaffId=" +
        StaffId +
        "&StoreId=" +
        StoreId +
        "&Date=" +
        Date +
        "&endDate=" +
        endDate;
      return baseService
        .getRequest(url)
        .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
          runInAction(() => {
            if (response.data.Error) {
              this.getStaffAppointmentsState.error = response.data.Message;
            } else {
              let data = response.data.Data;
              this.appointments = data.flatMap(
                (appointment: any) => appointment.AppointmentsList || []
              );
              // this.breaks = data.flatMap((staff: any) => staff.Breaks || []);
            }
          });
        })
        .catch((err: string) => {
          this.getStaffAppointmentsState.error = err;
        })
        .finally(
          action(() => {
            this.getStaffAppointmentsState.inProgress = false;
          })
        );
    }
  );

  setBreakIsShowFalse(breakId: number | string): boolean {
    const idx = this.breaks.findIndex((b) => b.BreakID === breakId);
    if (idx === -1) return false;

    // Two safe update options — choose one based on your observable setup:
    // Option A: mutate property directly (works when break objects are observable)
    // this.breaks[idx].IsShow = false;

    // Option B (safer if break objects are plain): replace the object so MobX detects change
    this.breaks[idx] = { ...this.breaks[idx], IsShow: false };

    return true;
  }
  // Computed property to get staff appointments with overlap logic
  get getStaffAppointments() {
    if (!this.events || this.events.length === 0) return [];

    // 🔹 Precompute event start/end times
    const eventTimes = this.events.map((event) => {
      const dateStr = moment(event.Date).format("YYYY-MM-DD");
      return {
        ...event,
        _dateStr: dateStr,
        _start: moment(`${dateStr} ${this.padTime(event.StartTime)}`, "YYYY-MM-DD HH:mm").toDate(),
        _end: moment(`${dateStr} ${this.padTime(event.EndTime)}`, "YYYY-MM-DD HH:mm").toDate(),
      };
    });

    // 🔹 Precompute breaks map by staffId + date
    const breaksMap = new Map<string, { BreakID: number; Start: Date; End: Date }[]>();
    (this.breaks || []).forEach((br) => {
      const dateStr = moment(br.Date).format("YYYY-MM-DD");
      const key = `${br.StaffId}-${dateStr}`;
      const brObj = {
        BreakID: br.BreakID,
        Start: moment(`${dateStr} ${this.padTime(br.StartTime)}`, "YYYY-MM-DD HH:mm").toDate(),
        End: moment(`${dateStr} ${this.padTime(br.EndTime)}`, "YYYY-MM-DD HH:mm").toDate(),
      };
      if (!breaksMap.has(key)) breaksMap.set(key, []);
      breaksMap.get(key)!.push(brObj);
    });

    // 🔹 Map of events by staffId + date for fast overlapping lookup
    const eventsMap = new Map<string, typeof eventTimes>();
    eventTimes.forEach((ev) => {
      const key = `${ev.StaffId}-${ev._dateStr}`;
      if (!eventsMap.has(key)) eventsMap.set(key, []);
      eventsMap.get(key)!.push(ev);
    });

    // 🔹 Compute final event objects
    return eventTimes.map((event) => {
      const key = `${event.StaffId}-${event._dateStr}`;
      const staffEvents = eventsMap.get(key)!;

      // 🔹 Overlapping events (direct)
      const overlappingEvents = staffEvents.filter(
        (other) => event._start < other._end && event._end > other._start
      );

      // 🔹 🔧 FIX: Expand overlaps transitively (include chained overlaps)
      const visited = new Set<number>();
      const stack = [...overlappingEvents.map((e) => e.Id), event.Id];
      const allOverlaps = new Set<number>([event.Id]);

      while (stack.length) {
        const currId = stack.pop()!;
        if (visited.has(currId)) continue;
        visited.add(currId);
        const currEvent = staffEvents.find((e) => e.Id === currId);
        if (!currEvent) continue;

        const directOverlaps = staffEvents.filter(
          (other) =>
            currEvent._start < other._end &&
            currEvent._end > other._start
        );

        for (const o of directOverlaps) {
          if (!allOverlaps.has(o.Id)) {
            allOverlaps.add(o.Id);
            stack.push(o.Id);
          }
        }
      }

      const overlappedIds = Array.from(allOverlaps);

      // 🔹 Sort overlapping events by start time
      const sortedOverlapping = staffEvents
        .filter((ev) => overlappedIds.includes(ev.Id))
        .sort((a, b) => a._start.getTime() - b._start.getTime());
      const indexInOverlap = sortedOverlapping.findIndex((ev) => ev.Id === event.Id);

      // 🔹 Overlapping breaks (current + overlapping events)
      let overlappedBreaks: { BreakID: number; IsShow: boolean }[] = [];

      const getBreaks = (ev: typeof event) => {
        const brKey = `${ev.StaffId}-${ev._dateStr}`;
        return (breaksMap.get(brKey) || [])
          .filter((br) => ev._start < br.End && ev._end > br.Start)
          .map((br) => ({ BreakID: br.BreakID, IsShow: false }));
      };

      overlappedBreaks = [
        ...getBreaks(event),
        ...sortedOverlapping.flatMap((ev) => getBreaks(ev)),
      ];

      // 🔹 Deduplicate breaks
      const uniqueBreaksMap = new Map<number, { BreakID: number; IsShow: boolean }>();
      overlappedBreaks.forEach((br) => {
        if (!uniqueBreaksMap.has(br.BreakID)) uniqueBreaksMap.set(br.BreakID, br);
      });
      overlappedBreaks = Array.from(uniqueBreaksMap.values());

      // 🔹 Determine IsShow for breaks
      if (overlappedIds.length === 1) {
        if (overlappedBreaks.length === 1) {
          overlappedBreaks[0].IsShow = true;
        } else {
          overlappedBreaks.forEach((br, idx) => (br.IsShow = idx === 0));
        }
      } else {
        overlappedBreaks.forEach((br) => (br.IsShow = false));
      }

      // 🔹 Prepare final event object
      const resultEvent = {
        ...event,
        Overlapped: overlappedIds.length,
        OverlappedEventsIds: overlappedIds,
        OverlappedBreaks: overlappedBreaks,
        HasBreakConflict: overlappedBreaks.length > 0,
        IsShow: false,
        IsMoreEvents: false,
      };

      // 🔹 Determine which overlapping events to show
      if (overlappedIds.length <= 2) {
        resultEvent.IsShow = true;
      } else {
        if (indexInOverlap === 0) resultEvent.IsShow = true;
        if (indexInOverlap === 1) {
          resultEvent.IsShow = true;
          resultEvent.IsMoreEvents = true;
        }
      }

      return resultEvent;
    });
  }

  get getStaffAppointmentsDate() {
    if (this.appointments && this.appointments.length > 0) {
      return this.appointments.map((event: IEvent) => {
        const eventDate = new Date(event.Date).toISOString().split("T")[0];
        const eventStart = new Date(
          `${eventDate}T${this.padTime(event.StartTime)}`
        );
        const eventEnd = new Date(
          `${eventDate}T${this.padTime(event.EndTime)}`
        );

        // Filter overlapping events
        const overlappingEvents = this.appointments.filter((other) => {
          const otherDate = new Date(other.Date).toISOString().split("T")[0];

          // Compare normalized dates
          if (otherDate !== eventDate) return false;

          // Compare staff
          if (other.StaffId !== event.StaffId) return false;

          // Compare time
          const otherStart = new Date(
            `${otherDate}T${this.padTime(other.StartTime)}`
          );
          const otherEnd = new Date(
            `${otherDate}T${this.padTime(other.EndTime)}`
          );

          return eventStart < otherEnd && eventEnd > otherStart;
        });

        const overlappedIds = overlappingEvents.map((ev) => ev.Id);

        // Always include self
        if (!overlappedIds.includes(event.Id)) {
          overlappedIds.push(event.Id);
        }

        // Sort overlapping events by start time
        const sortedOverlapping = [...overlappingEvents].sort((a, b) => {
          const aStart = new Date(`${eventDate}T${this.padTime(a.StartTime)}`);
          const bStart = new Date(`${eventDate}T${this.padTime(b.StartTime)}`);
          return aStart.getTime() - bStart.getTime();
        });

        const indexInOverlap = sortedOverlapping.findIndex(
          (ev) => ev.Id === event.Id
        );

        const resultEvent = {
          ...event,
          Overlapped: overlappedIds.length, // Number of overlapping events
          OverlappedEventsIds: overlappedIds, // IDs of overlapping events
          IsShow: false, // Whether to show the event
          IsMoreEvents: false, // Whether there are more events
        };

        if (overlappedIds.length <= 2) {
          resultEvent.IsShow = true;
          resultEvent.IsMoreEvents = false;
        } else {
          if (indexInOverlap === 0) {
            resultEvent.IsShow = true;
            resultEvent.IsMoreEvents = false;
          } else if (indexInOverlap === 1) {
            resultEvent.IsShow = true;
            resultEvent.IsMoreEvents = true;
          } else {
            resultEvent.IsShow = false;
            resultEvent.IsMoreEvents = false;
          }
        }

        return resultEvent;
      });
    }

    return [];
  }

  getStaffAppointmentCountByDateRange = (
    selectedStaffId: number,
    startDate: string,
    endDate: string
  ) => {
    const start = moment(startDate, "YYYY-MM-DD");
    const end = moment(endDate, "YYYY-MM-DD");

    const result: { date: string; count: number; totalMinutes: number }[] = [];

    for (let m = moment(start); m.isSameOrBefore(end); m.add(1, "day")) {
      const currentDateStr = m.format("YYYY-MM-DD");

      const dailyAppointments = this.getStaffAppointments.filter(
        (appt) =>
        {
          if(selectedStaffId == -1){
            return moment(appt.Date).format("YYYY-MM-DD") === currentDateStr
          }else{
            return appt.StaffId === selectedStaffId && moment(appt.Date).format("YYYY-MM-DD") === currentDateStr
          }
        }
      );

      let totalMinutes = 0;

      dailyAppointments.forEach((appt) => {
        const start = moment(`${appt.Date} ${appt.StartTime}`, "YYYY-MM-DD HH:mm");
        const end = moment(`${appt.Date} ${appt.EndTime}`, "YYYY-MM-DD HH:mm");

        if (start.isValid() && end.isValid() && end.isAfter(start)) {
          totalMinutes += end.diff(start, "minutes");
        }
      });

      result.push({
        date: currentDateStr,
        count: dailyAppointments.length,
        totalMinutes,
      });
    }

    return result;
  };

  get getStaffAppointmentsNonStaff() {
    if (this.events && this.events.length > 0) {
      return this.events.map((event: IEvent) => {
        // ✅ Use moment for date normalization (local time)
        const eventDate = moment(event.Date).format("YYYY-MM-DD");
        const eventStart = moment(
          `${eventDate} ${this.padTime(event.StartTime)}`,
          "YYYY-MM-DD HH:mm"
        ).toDate();
        const eventEnd = moment(
          `${eventDate} ${this.padTime(event.EndTime)}`,
          "YYYY-MM-DD HH:mm"
        ).toDate();

        // 🔹 Existing: Filter overlapping events (direct)
        const overlappingEvents = this.events.filter((other) => {
          const otherDate = moment(other.Date).format("YYYY-MM-DD");
          if (otherDate !== eventDate) return false;

          const otherStart = moment(
            `${otherDate} ${this.padTime(other.StartTime)}`,
            "YYYY-MM-DD HH:mm"
          ).toDate();
          const otherEnd = moment(
            `${otherDate} ${this.padTime(other.EndTime)}`,
            "YYYY-MM-DD HH:mm"
          ).toDate();

          return eventStart < otherEnd && eventEnd > otherStart;
        });

        // 🔹 🔧 FIX: Expand overlaps transitively (include chained overlaps)
        const visited = new Set<number>();
        const stack = [...overlappingEvents.map(e => e.Id), event.Id];
        const allOverlaps = new Set<number>([event.Id]);

        while (stack.length) {
          const currId = stack.pop()!;
          if (visited.has(currId)) continue;
          visited.add(currId);

          const currEvent = this.events.find(e => e.Id === currId);
          if (!currEvent) continue;

          const currEventDate = moment(currEvent.Date).format("YYYY-MM-DD");
          const currStart = moment(
            `${currEventDate} ${this.padTime(currEvent.StartTime)}`,
            "YYYY-MM-DD HH:mm"
          ).toDate();
          const currEnd = moment(
            `${currEventDate} ${this.padTime(currEvent.EndTime)}`,
            "YYYY-MM-DD HH:mm"
          ).toDate();

          const directOverlaps = this.events.filter((other) => {
            const otherDate = moment(other.Date).format("YYYY-MM-DD");
            if (otherDate !== currEventDate) return false;

            const otherStart = moment(
              `${otherDate} ${this.padTime(other.StartTime)}`,
              "YYYY-MM-DD HH:mm"
            ).toDate();
            const otherEnd = moment(
              `${otherDate} ${this.padTime(other.EndTime)}`,
              "YYYY-MM-DD HH:mm"
            ).toDate();

            return currStart < otherEnd && currEnd > otherStart;
          });

          for (const o of directOverlaps) {
            if (!allOverlaps.has(o.Id)) {
              allOverlaps.add(o.Id);
              stack.push(o.Id);
            }
          }
        }

        const overlappedIds = Array.from(allOverlaps);

        // Sort overlapping events by start time
        const sortedOverlapping = [...overlappingEvents].sort((a, b) => {
          const aStart = moment(
            `${eventDate} ${this.padTime(a.StartTime)}`,
            "YYYY-MM-DD HH:mm"
          ).toDate();
          const bStart = moment(
            `${eventDate} ${this.padTime(b.StartTime)}`,
            "YYYY-MM-DD HH:mm"
          ).toDate();
          return aStart.getTime() - bStart.getTime();
        });

        const indexInOverlap = sortedOverlapping.findIndex(
          (ev) => ev.Id === event.Id
        );

        // 🔹 Check overlap with breaks
        const overlappedBreaks =
          this.breaks
            ?.filter((br) => {
              const breakDate = moment(br.Date).format("YYYY-MM-DD");
              if (breakDate !== eventDate) return false;

              const breakStart = moment(
                `${breakDate} ${this.padTime(br.StartTime)}`,
                "YYYY-MM-DD HH:mm"
              ).toDate();
              const breakEnd = moment(
                `${breakDate} ${this.padTime(br.EndTime)}`,
                "YYYY-MM-DD HH:mm"
              ).toDate();

              // ✅ Proper overlap rule
              return eventStart < breakEnd && eventEnd > breakStart;
            })
            .map((br) => ({ BreakID: br.BreakID, IsShow: false })) || [];

        // 🔹 Handle IsShow for breaks
        if (overlappedIds.length === 1) {
          if (overlappedBreaks.length === 1) {
            overlappedBreaks[0].IsShow = true;
          } else if (overlappedBreaks.length > 1) {
            overlappedBreaks.forEach((br, idx) => (br.IsShow = idx === 0));
          }
        } else if (overlappedIds.length > 1) {
          overlappedBreaks.forEach((br) => (br.IsShow = false));
        }

        const resultEvent = {
          ...event,
          Overlapped: overlappedIds.length,
          OverlappedEventsIds: overlappedIds,
          OverlappedBreaks: overlappedBreaks,
          HasBreakConflict: overlappedBreaks.length > 0,
          IsShow: false,
          IsMoreEvents: false,
        };

        if (overlappedIds.length <= 2) {
          if((resultEvent.OverlappedBreaks.length > 0) && (indexInOverlap == 1)){
            resultEvent.IsShow = true;
            resultEvent.IsMoreEvents = true;
          }
          else{
            resultEvent.IsShow = true;
            resultEvent.IsMoreEvents = false;
          }
        } else {
          if (indexInOverlap === 0) {
            resultEvent.IsShow = true;
            resultEvent.IsMoreEvents = false;
          } else if (indexInOverlap === 1) {
            resultEvent.IsShow = true;
            resultEvent.IsMoreEvents = true;
          } else {
            resultEvent.IsShow = false;
            resultEvent.IsMoreEvents = false;
          }
        }

        return resultEvent;
      });
    }

    return [];
  }

  get getSelectedStaffIdWeekly() {
    return this.selectedStaffIdWeekly; // Getter for selected staff ID in weekly view
  }

  // Pads time strings to ensure consistent formatting
  padTime(t: string): string {
    const [h, m] = t.split(":");
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  }

  get getBreaks() {
    if (this.breaks && this.breaks.length > 0) {
      return this.breaks.map((current) => {
        const overlappedBreakIds: any = [];

        const currentDate = new Date(current.Date)?.toISOString()?.slice(0, 10);
        const [currentStartHour, currentStartMin] =
          current.StartTime.split(":").map(Number);
        const [currentEndHour, currentEndMin] =
          current.EndTime.split(":").map(Number);
        const currentStart = currentStartHour * 60 + currentStartMin;
        const currentEnd = currentEndHour * 60 + currentEndMin;

        // 🔹 Find overlapping breaks
        const overlappingBreaks = this.breaks.filter((other) => {
          if (current.BreakID === other.BreakID) return false;
          if (current.StaffId !== other.StaffId) return false;

          const otherDate = new Date(other.Date).toISOString().slice(0, 10);
          if (currentDate !== otherDate) return false;

          const [otherStartHour, otherStartMin] =
            other.StartTime.split(":").map(Number);
          const [otherEndHour, otherEndMin] =
            other.EndTime.split(":").map(Number);
          const otherStart = otherStartHour * 60 + otherStartMin;
          const otherEnd = otherEndHour * 60 + otherEndMin;

          return currentStart < otherEnd && currentEnd > otherStart;
        });

        const overlappedBreakIdsList = overlappingBreaks.map((b) => b.BreakID);

        // ✅ Include self when sorting overlaps
        const allOverlap = [...overlappingBreaks, current].sort((a, b) => {
          const [aH, aM] = a.StartTime.split(":").map(Number);
          const [bH, bM] = b.StartTime.split(":").map(Number);
          return aH * 60 + aM - (bH * 60 + bM);
        });

        const indexInOverlap = allOverlap.findIndex(
          (br) => br.BreakID === current.BreakID
        );

        // 🔹 Default visibility logic
        let isShow = false;
        let isMoreBreaks = false;

        if (allOverlap.length <= 2) {
          isShow = true;
          isMoreBreaks = false;
        } else {
          if (indexInOverlap === 0) {
            isShow = true;
            isMoreBreaks = false;
          } else if (indexInOverlap === 1) {
            isShow = true;
            isMoreBreaks = true;
          } else {
            isShow = false;
            isMoreBreaks = false;
          }
        }

        // 🔹 Extra: check overlap with events
        const hasEventConflict =
          this.events?.some((ev) => {
            const evDate = new Date(ev.Date).toISOString().slice(0, 10);
            if (evDate !== currentDate) return false;
            if (ev.StaffId !== current.StaffId) return false;

            const [evStartH, evStartM] = ev.StartTime.split(":").map(Number);
            const [evEndH, evEndM] = ev.EndTime.split(":").map(Number);
            const evStart = evStartH * 60 + evStartM;
            const evEnd = evEndH * 60 + evEndM;

            return currentStart < evEnd && currentEnd > evStart;
          }) || false;

        // If overlapping with events → force hide
        if (hasEventConflict) {
          isShow = false;
        }

        return {
          ...current,
          Overlapped: overlappedBreakIdsList.length,
          OverlappedBreakIds: overlappedBreakIdsList,
          IsShow: isShow,
          IsMoreBreaks: isMoreBreaks,
          HasEventConflict: hasEventConflict,
        };
      });
    } else {
      return [];
    }
  }

get getStaffBreaks() {
  if (!this.breaks || this.breaks.length === 0) return [];

  // 🔹 Precompute event start/end times (keep as moment objects)
  const eventTimes = this.breaks.map((event) => {
    const dateStr = moment(event.Date).format("YYYY-MM-DD");
    return {
      ...event,
      _dateStr: dateStr,
      _start: moment(`${dateStr} ${this.padTime(event.StartTime)}`, "YYYY-MM-DD HH:mm"),
      _end: moment(`${dateStr} ${this.padTime(event.EndTime)}`, "YYYY-MM-DD HH:mm"),
    };
  });

  // 🔹 Build map of breaks per date (no staffId)
  const breaksMap = new Map<string, { BreakID: number; Start: moment.Moment; End: moment.Moment }[]>();
  (this.breaks || []).forEach((br) => {
    const dateStr = moment(br.Date).format("YYYY-MM-DD");
    const brObj = {
      BreakID: br.BreakID,
      Start: moment(`${dateStr} ${this.padTime(br.StartTime)}`, "YYYY-MM-DD HH:mm"),
      End: moment(`${dateStr} ${this.padTime(br.EndTime)}`, "YYYY-MM-DD HH:mm"),
    };
    if (!breaksMap.has(dateStr)) breaksMap.set(dateStr, []);
    breaksMap.get(dateStr)!.push(brObj);
  });

  // 🔹 Group events by date (no staffId)
  const eventsMap = new Map<string, typeof eventTimes>();
  eventTimes.forEach((ev) => {
    if (!eventsMap.has(ev._dateStr)) eventsMap.set(ev._dateStr, []);
    eventsMap.get(ev._dateStr)!.push(ev);
  });

  // 🔹 Compute final event objects
  return eventTimes.map((event) => {
    const dateEvents = eventsMap.get(event._dateStr)!;

    // 🔹 Find all overlapping events (chain detection)
    const visited = new Set<number>();
    const stack = [event.BreakID];
    const allOverlaps = new Set<number>([event.BreakID]);

    while (stack.length) {
      const currId = stack.pop()!;
      if (visited.has(currId)) continue;
      visited.add(currId);

      const currEvent = dateEvents.find((e) => e.BreakID === currId);
      if (!currEvent) continue;

      const directOverlaps = dateEvents.filter(
        (other) =>
          currEvent._start.isBefore(other._end) &&
          currEvent._end.isAfter(other._start)
      );

      for (const o of directOverlaps) {
        if (!allOverlaps.has(o.BreakID)) {
          allOverlaps.add(o.BreakID);
          stack.push(o.BreakID);
        }
      }
    }

    const overlappedIds = Array.from(allOverlaps);

    // 🔹 Sort overlapping events by start time
    const sortedOverlapping = dateEvents
      .filter((ev) => overlappedIds.includes(ev.BreakID))
      .sort((a, b) => a._start.valueOf() - b._start.valueOf());

    const indexInOverlap = sortedOverlapping.findIndex(
      (ev) => ev.BreakID === event.BreakID
    );

    // 🔹 Collect overlapped breaks
    const overlappedBreaksMap = new Map<number, { BreakID: number; IsShow: boolean }>();

    const getBreaks = (ev: typeof event) => {
      return (breaksMap.get(ev._dateStr) || []).filter(
        (br) => ev._start.isBefore(br.End) && ev._end.isAfter(br.Start)
      );
    };

    for (const ev of sortedOverlapping) {
      const brs = getBreaks(ev);
      for (const br of brs) {
        if (!overlappedBreaksMap.has(br.BreakID)) {
          overlappedBreaksMap.set(br.BreakID, { BreakID: br.BreakID, IsShow: false });
        }
      }
    }

    const overlappedBreaks = Array.from(overlappedBreaksMap.values());

    // 🔹 Determine which events to show
    let isShow = false;
    let isMoreEvents = false;

    if (sortedOverlapping.length <= 2) {
      isShow = true;
    } else {
      if (indexInOverlap === 0) isShow = true;
      if (indexInOverlap === 1) {
        isShow = true;
        isMoreEvents = true;
      }
    }

    // 🔹 Break visibility logic
    if (overlappedIds.length === 1) {
      if (overlappedBreaks.length === 1) {
        overlappedBreaks[0].IsShow = true;
      } else {
        overlappedBreaks.forEach((br, idx) => (br.IsShow = idx === 0));
      }
    } else {
      overlappedBreaks.forEach((br) => (br.IsShow = false));
    }

    // ✅ Final object
    return {
      ...event,
      Overlapped: overlappedIds.length,
      OverlappedEventsIds: overlappedIds,
      OverlappedBreaks: overlappedBreaks,
      HasBreakConflict: overlappedBreaks.length > 0,
      IsShow: isShow,
      IsMoreEvents: isMoreEvents,
    };
  });
}


  // Adds a staff break via API
  AddStaffBreakService = (data: any) => {
    this.addStaffBreakState.inProgress = true;
    let url = URLConstants.AddStaffBreak;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.addStaffBreakState.error = response.data.Message;
          toast.error(formatMessage(response?.data?.Message));
        } else this.addStaffBreakState.success = true;
      })
      .catch((err: string) => {
        this.addStaffBreakState.error = err;
      })
      .finally(
        action(() => {
          this.addStaffBreakState.inProgress = false;
        })
      );
  };

  // Resets the add staff break state
  resetAddStaffBreakState = () => {
    this.addStaffBreakState = { ...this.initialStateValue };
  };

  // Updates draggable appointment via API
  UpdateAppointmentDraggableService = (data: any) => {
    this.updateDraggableAppointmentState.inProgress = true;
    this.updateDraggableAppointmentState.error = "";
    this.updateDraggableAppointmentState.success = false;
    this.isPollingActive = true; // flag to control polling lifecycle
    let url = URLConstants.UpdateAppointmentDraggable;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.updateDraggableAppointmentState.error = response.data.Message;
          if (response.data.Message) {
            toast.error(formatMessage(response?.data?.Message));
          }
        } else{
          const stateId = response.data.Data;
          if (stateId == null) {
            // Defensive: no stateId returned
            this.updateDraggableAppointmentState.error = "NoStateIdReturned";
            this.updateDraggableAppointmentState.inProgress = false;
            // console.log(
            //   "No StateId returned from AddWalkInCustomerAppointment API"
            // );
            return;
          }
                      // Start polling with the stateId
          this.pollReservationProcessState(stateId);
        }
      })
    .catch(
      action((err: any) => {
        this.updateDraggableAppointmentState.error =
          err?.message ||
          err?.toString() ||
          "Error Dragging appointment";
        toast.error(this.updateDraggableAppointmentState.error);
        this.updateDraggableAppointmentState.inProgress = false;
      })
    );
  };

  // Polling function
  pollReservationProcessState = (stateId: number) => {
    if (!this.isPollingActive) {
      // Polling stopped externally (e.g., on unmount)
      this.getReservationListState.inProgress = false;
      return;
    }

    this.getReservationListState.inProgress = true;

    this.GetReservationProcessStateService(stateId)
      .then(
        action(
          (
            response: IApiResponse<
              IApiSuccessResponse<IReservationProcessStateVM>
            >
          ) => {
            if (response.data.Error) {
              this.getReservationListState.error = response.data.Message;
              toast.error(formatMessage(response.data.Message));
              this.getReservationListState.inProgress = false;
              this.isPollingActive = false; // stop polling on error
            } else {
              const data = response.data.Data;
              this.reservationProcessStateResponse = data;
              this.getReservationListState.error = "";

              if (data.Status === AppointmentProcessState.Complete) {
                this.updateDraggableAppointmentState.inProgress = false;
                this.getReservationListState.inProgress = false;
                this.updateDraggableAppointmentState.success = true;
                // console.log("Process state is Success!");
                this.isPollingActive = false; // done polling
              } else if (data.Status === AppointmentProcessState.Error) {
                this.updateDraggableAppointmentState.inProgress = false;
                this.updateDraggableAppointmentState.error = data.Response;
                this.isPollingActive = false;
                // console.log("Error in the Polling Request");
              } else {
                // polling not complete, continue after 1 second
                setTimeout(() => {
                  this.pollReservationProcessState(stateId);
                }, 1000);
              }
            }
          }
        )
      )
      .catch(
        action((err: any) => {
          this.getReservationListState.error =
            err?.message ||
            err?.toString() ||
            "Error polling reservation process state";
          console.log(this.getReservationListState.error);
          this.getReservationListState.inProgress = false;
          this.isPollingActive = false; // stop polling on error
        })
      );
  };

  // Modified GetReservationProcessStateService - NO inProgress mutation here
  GetReservationProcessStateService = (stateId: number) => {
    const url =
      URLConstants.GetReservationProcessStateById +
      "RequestStatus?Id=" +
      stateId;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<
            IApiSuccessResponse<IReservationProcessStateVM>
          >
        ) => {
          if (response.data.Error) {
            this.getReservationListState.error = response.data.Message;
          } else {
            this.reservationProcessStateResponse = response.data.Data;
            this.getReservationListState.error = "";
          }
          return response;
        }
      )
      .catch((err: any) => {
        this.getReservationListState.error =
          err?.message || err?.toString() || "API Error";
        throw err; // propagate error to poller
      });
  };

  // Optional: method to stop polling if needed (e.g., on component unmount)
  stopPolling = () => {
    this.isPollingActive = false;
    this.getReservationListState.inProgress = false;
  };

  // Resets the update appointment state
  resetUpdateAppointmentState = () => {
    this.updateDraggableAppointmentState = { ...this.initialStateValue };
  };

  // Updates draggable break via API
  UpdateBreakDraggableService = (data: any) => {
    this.updateDraggableBreakState.inProgress = true;
    let url = URLConstants.UpdateStaffBreak;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.updateDraggableBreakState.error = response.data.Message;
          toast.error(response?.data?.Message);
        } else this.updateDraggableBreakState.success = true;
      })
      .catch((err: string) => {
        this.updateDraggableBreakState.error = err;
      })
      .finally(
        action(() => {
          this.updateDraggableBreakState.inProgress = false;
        })
      );
  };

  // Resets the update staff break state
  resetUpdateStaffBreakState = () => {
    this.updateDraggableBreakState = { ...this.initialStateValue };
  };

  // Fetches staff break by ID via API
  GetStaffBreakByIdService = (id: number) => {
    this.getStaffBreakByIdState.inProgress = true;
    let url = `${URLConstants.GetStaffBreakById}/${id}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.getStaffBreakByIdState.error = response.data.Message;
        } else {
          let data = response.data.Data;
          this.staffBreak = data;
        }
      })
      .catch((err: string) => {
        this.getStaffBreakByIdState.error = err;
      })
      .finally(
        action(() => {
          this.getStaffBreakByIdState.inProgress = false;
        })
      );
  };

  // Computed property to get staff break details
  get staffBreakDetails(): IBreakVM {
    if (this.staffBreak) {
      return {
        Id: this.staffBreak.Id, // Staff break ID
        Title: this.staffBreak.Title, // Staff break title
        Date: this.staffBreak.Date, // Staff break date
        StartTime: moment(
          `${localStorage.getItem("currentDate")}T${this.staffBreak.StartTime}`
        ), // Staff break start time
        EndTime: moment(
          `${localStorage.getItem("currentDate")}T${this.staffBreak.EndTime}`
        ), // Staff break end time
        StaffId: this.staffBreak.StaffId, // Staff ID
        StoreId: this.staffBreak?.StoreId, // Store ID
        TotalTime: this.staffBreak?.TotalTime, // Total break time
        Remarks: this.staffBreak.Remarks, // Remarks for the break
        BreakType: this.staffBreak.BreakType, // Type of break
      };
    }
    return AddBreakState;
  }

  // Resets the get staff break by ID state
  resetGetStaffBreakByIdState = () => {
    this.staffBreak = undefined;
    this.getStaffBreakByIdState = { ...this.initialStateValue };
  };

  DeleteStaffBreaksService = (Id: number) => {
    this.deleteStaffBreakByIdState.inProgress = true;
    let url = `${URLConstants.DeleteStaffBreak}/${Id}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteStaffBreakByIdState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else this.deleteStaffBreakByIdState.success = true;
      })
      .catch((err: string) => {
        this.deleteStaffBreakByIdState.error = err;
      })
      .finally(
        action(() => {
          this.deleteStaffBreakByIdState.inProgress = false;
        })
      );
  };

  resetDeleteStaffBreakState = () => {
    this.deleteStaffBreakByIdState = { ...this.initialStateValue };
  };

  UpdateReservationMemoService = (data: any) => {
    this.updateReservationMemoState.inProgress = true;
    let url = URLConstants.UpdateReservationMemo;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.updateReservationMemoState.error = response.data.Message;
        } else this.updateReservationMemoState.success = true;
      })
      .catch((err: string) => {
        this.updateReservationMemoState.error = err;
      })
      .finally(
        action(() => {
          this.updateReservationMemoState.inProgress = false;
        })
      );
  };

  UpdateReservationCancelStatusService = (data: any) => {
    this.updateReservationCancelState.inProgress = true;
    let url = URLConstants.UpdateReservationCancel;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.updateReservationCancelState.error = response.data.Message;
        } else this.updateReservationCancelState.success = true;
      })
      .catch((err: string) => {
        this.updateReservationCancelState.error = err;
      })
      .finally(
        action(() => {
          this.updateReservationCancelState.inProgress = false;
        })
      );
  };

  UpdateBookingTypeService = (data: any) => {
    this.updateBookingTypeState.inProgress = true;
    let url = URLConstants.UpdateBookingType;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.updateBookingTypeState.error = response.data.Message;
        } else this.updateBookingTypeState.success = true;
      })
      .catch((err: string) => {
        this.updateBookingTypeState.error = err;
      })
      .finally(
        action(() => {
          this.updateBookingTypeState.inProgress = false;
        })
      );
  };

  resetBookingTypeState = () => {
    this.updateBookingTypeState = { ...this.initialStateValue };
  };

  UpdateReservationPostBreakTimeService = (data: any) => {
    this.updateReservationCancelState.inProgress = true;
    let url = URLConstants.UpdateReservationPostBreakTime;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.updateReservationCancelState.error = response.data.Message;
        } else this.updateReservationCancelState.success = true;
      })
      .catch((err: string) => {
        this.updateReservationCancelState.error = err;
      })
      .finally(
        action(() => {
          this.updateReservationCancelState.inProgress = false;
        })
      );
  };

  resetUpdateReservationMemoState = () => {
    this.updateReservationMemoState = { ...this.initialStateValue };
  };

  resetUpdateReservationCancelState = () => {
    this.updateReservationCancelState = { ...this.initialStateValue };
  };

  resetDroppedEvents = () => {
    this.droppedEvents = [];
  };
}

export default new ReservationSchedulerStore(); // Exporting the store instance
